// Enhanced Logger utility with Sentry integration
// Development logs are printed to console, production errors are sent to Sentry
//
// USAGE EXAMPLES:
// import { log, logInfo, logError, logWarn, logDebug, logSuccess } from '@/shared/utils/logger.js';
//
// // Basic logging
// log("User logged in successfully");
// logInfo("Processing request", { userId: 123 });
// logError("Database connection failed", error);
// logWarn("Rate limit approaching", { requests: 95 });
// logDebug("Cache hit", { key: "user:123" });
// logSuccess("Payment processed", { amount: 100 });
//
// OUTPUT FORMAT:
// [2024-01-15T10:30:45.123Z] [INFO] User logged in successfully
// [2024-01-15T10:30:45.124Z] [ERROR] Database connection failed
// [2024-01-15T10:30:45.125Z] [WARN] Rate limit approaching

import * as Sentry from "@sentry/react";
import { shouldReportLogToSentry } from "./sentryEventFilters.js";

const isDevelopment = () => import.meta.env.DEV;
const isProduction = () => import.meta.env.MODE === "production";

/**
 * Format log message with timestamp and level
 * @param {string} level - Log level (info, error, warn, debug)
 * @param {...any} args - Log arguments
 * @returns {Array} Formatted log arguments
 */
function formatLogMessage(level, ...args) {
  const timestamp = new Date().toISOString();
  const levelColor = getLevelColor(level);
  const resetColor = "\x1b[0m";

  return [
    `${levelColor}[${timestamp}] [${level.toUpperCase()}]${resetColor}`,
    ...args,
  ];
}

/**
 * Get color code for log level
 * @param {string} level - Log level
 * @returns {string} Color code
 */
function getLevelColor(level) {
  const colors = {
    info: "\x1b[36m", // Cyan
    error: "\x1b[31m", // Red
    warn: "\x1b[33m", // Yellow
    debug: "\x1b[35m", // Magenta
    success: "\x1b[32m", // Green
  };
  return colors[level] || "\x1b[0m"; // Default to no color
}

/**
 * Log info messages (development only)
 * @param {...any} args - Log arguments
 */
export function logInfo(...args) {
  if (isDevelopment()) {
    console.log(...formatLogMessage("info", ...args));
  }
}

/**
 * Send error to Sentry in production
 * @param {Error|string} error - Error object or message
 * @param {Object} context - Additional context
 */
function sendToSentry(error, context = {}) {
  if (!isProduction()) return;
  if (!shouldReportLogToSentry(error, context)) return;

  try {
    if (error instanceof Error) {
      Sentry.captureException(error, {
        extra: context,
        tags: {
          logger: "enhanced-logger",
          level: "error",
        },
      });
    } else {
      Sentry.captureMessage(String(error), "error", {
        extra: context,
        tags: {
          logger: "enhanced-logger",
          level: "error",
        },
      });
    }
  } catch (sentryError) {
    // Fallback to console if Sentry fails
    console.error("Sentry error:", sentryError);
    console.error("Original error:", error);
  }
}

/**
 * Log error messages (development console + production Sentry)
 * @param {...any} args - Log arguments
 */
export function logError(...args) {
  // Always log to console in development
  if (isDevelopment()) {
    console.error(...formatLogMessage("error", ...args));
  }

  // Send to Sentry in production
  if (isProduction() && args.length > 0) {
    const [firstArg, ...restArgs] = args;
    const context = restArgs.length > 0 ? restArgs[0] : {};

    sendToSentry(firstArg, context);
  }
}

/**
 * Log warning messages (development only)
 * @param {...any} args - Log arguments
 */
export function logWarn(...args) {
  if (isDevelopment()) {
    console.warn(...formatLogMessage("warn", ...args));
  }
}

/**
 * Log debug messages (development only)
 * @param {...any} args - Log arguments
 */
export function logDebug(...args) {
  if (isDevelopment()) {
    console.log(...formatLogMessage("debug", ...args));
  }
}

/**
 * Log success messages (development only)
 * @param {...any} args - Log arguments
 */
export function logSuccess(...args) {
  if (isDevelopment()) {
    console.log(...formatLogMessage("success", ...args));
  }
}

/**
 * Default log function (development only)
 * @param {...any} args - Log arguments
 */
export function log(...args) {
  if (isDevelopment()) {
    console.log(...formatLogMessage("info", ...args));
  }
}

// Legacy function for backward compatibility
export function logDev(...args) {
  log(...args);
}

/**
 * Set user context for Sentry
 * @param {Object} user - User information
 */
export function setUserContext(user) {
  if (isProduction()) {
    try {
      Sentry.setUser(user);
    } catch (error) {
      console.error("Failed to set user context in Sentry:", error);
    }
  }
}

/**
 * Set tag for Sentry
 * @param {string} key - Tag key
 * @param {string} value - Tag value
 */
export function setTag(key, value) {
  if (isProduction()) {
    try {
      Sentry.setTag(key, value);
    } catch (error) {
      console.error("Failed to set tag in Sentry:", error);
    }
  }
}

/**
 * Set context for Sentry
 * @param {string} key - Context key
 * @param {Object} value - Context value
 */
export function setContext(key, value) {
  if (isProduction()) {
    try {
      Sentry.setContext(key, value);
    } catch (error) {
      console.error("Failed to set context in Sentry:", error);
    }
  }
}

/**
 * Add breadcrumb for Sentry
 * @param {Object} breadcrumb - Breadcrumb information
 */
export function addBreadcrumb(breadcrumb) {
  if (isProduction()) {
    try {
      Sentry.addBreadcrumb(breadcrumb);
    } catch (error) {
      console.error("Failed to add breadcrumb in Sentry:", error);
    }
  }
}

/**
 * Capture exception directly to Sentry
 * @param {Error} error - Error to capture
 * @param {Object} context - Additional context
 */
export function captureException(error, context = {}) {
  if (isProduction()) {
    try {
      Sentry.captureException(error, { extra: context });
    } catch (sentryError) {
      console.error("Failed to capture exception in Sentry:", sentryError);
    }
  }
}

/**
 * Capture message directly to Sentry
 * @param {string} message - Message to capture
 * @param {string} level - Log level
 * @param {Object} context - Additional context
 */
export function captureMessage(message, level = "info", context = {}) {
  if (isProduction()) {
    try {
      Sentry.captureMessage(message, level, { extra: context });
    } catch (sentryError) {
      console.error("Failed to capture message in Sentry:", sentryError);
    }
  }
}

// Export all log functions as default object
export default {
  log,
  logInfo,
  logError,
  logWarn,
  logDebug,
  logSuccess,
  logDev, // Legacy
  // Sentry integration functions
  setUserContext,
  setTag,
  setContext,
  addBreadcrumb,
  captureException,
  captureMessage,
};
